import { useQuery } from '@tanstack/react-query';
import constate from 'constate';

import { api, getQueryProps, isTruthy } from 'common';

import { useSubflowVersionQuery } from './useSubflowVersion.tsx';
import { useSharedSubflowBlockFields } from './utils.ts';
import { type SharedBlockFormFieldsProps } from '../../constants.tsx';

function useSubFlowFields(props: SharedBlockFormFieldsProps) {
  const { fieldNamePrefix, allowedFlowKinds, variant } = props;

  const { flowIdFieldProps, specificVersionFlowDefinitionIdFieldProps, versionTargetFieldProps } =
    useSharedSubflowBlockFields({ fieldNamePrefix });

  const flowIdFieldValue = flowIdFieldProps.value;

  /**
   * Get subflow for the given flowId kind (e.g. Base Flow or Skill)
   */
  const { data: selectedFlowAndFlowDefinitions } = useQuery({
    ...getQueryProps(api.client.Flow, 'findOneById', flowIdFieldValue ?? 'Invalid id'),
    enabled: isTruthy(flowIdFieldValue),
  });

  const { data: selectedSkillAndFlowDefinitions } = useQuery({
    ...getQueryProps(api.client.FlowBlock, 'findOneSkillById', flowIdFieldValue ?? 'Invalid id'),
    enabled: isTruthy(flowIdFieldValue),
  });
  const selectedSubflowAndFlowDefinitions =
    selectedFlowAndFlowDefinitions ?? selectedSkillAndFlowDefinitions;
  const selectedSubflow =
    selectedFlowAndFlowDefinitions?.flow ?? selectedSkillAndFlowDefinitions?.skill;

  const flowDefinitionResult = useSubflowVersionQuery({
    flowId: flowIdFieldProps.value,
    target: versionTargetFieldProps.value,
    flowDefinitionId: specificVersionFlowDefinitionIdFieldProps.value,
  });

  return {
    flowIdFieldProps,
    selectedSubflow,
    selectedSubflowAndFlowDefinitions,
    specificVersionFlowDefinitionIdFieldProps,
    versionTargetFieldProps,
    flowDefinitionResult,
    fieldNamePrefix,
    variant,
    allowedFlowKinds,
  };
}

export const [SubFlowFieldsProvider, useSubFlowFieldsContext] = constate(useSubFlowFields);
